import React, { useState, useEffect } from 'react';
import axios from 'axios';

function UsersPage({ apiUrl, auth }) {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', role: 'employee', permissions: { add_expenses: false } });

  useEffect(() => { loadUsers(); }, []);

  const headers = { Authorization: `Bearer ${auth.token}` };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${apiUrl}/auth/users`, { headers });
      setUsers(res.data);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const openAdd = () => {
    setEditUser(null);
    setForm({ username: '', password: '', role: 'employee', permissions: { add_expenses: false } });
    setShowModal(true);
  };

  const openEdit = (user) => {
    const perms = JSON.parse(user.permissions || '{}');
    setEditUser(user);
    setForm({
      username: user.username,
      password: '',
      role: user.role,
      permissions: { add_expenses: perms.add_expenses || perms.canAddExpenses || false }
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.username) return;
    if (!editUser && !form.password) return;
    try {
      const permissions = JSON.stringify({ canAddExpenses: form.permissions.add_expenses });
      if (editUser) {
        const updateData = { username: form.username, role: form.role, permissions };
        if (form.password) updateData.password = form.password;
        await axios.put(`${apiUrl}/auth/users/${editUser.id}`, updateData, { headers });
      } else {
        await axios.post(`${apiUrl}/auth/register`, {
          username: form.username,
          password: form.password,
          role: form.role,
          permissions,
        }, { headers });
      }
      setShowModal(false);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (user) => {
    if (user.id === auth.user.id) {
      alert('Vous ne pouvez pas supprimer votre propre compte');
      return;
    }
    if (!window.confirm(`Supprimer l'utilisateur "${user.username}" ?`)) return;
    try {
      await axios.delete(`${apiUrl}/auth/users/${user.id}`, { headers });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const getRoleLabel = (role) => {
    if (role === 'admin') return 'Administrateur';
    if (role === 'seller') return 'Vendeur';
    return 'Employé';
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') return 'badge-info';
    if (role === 'seller') return 'badge-warning';
    return 'badge-success';
  };

  return (
    <div>
      <div className="page-header">
        <h1>👥 Gestion des utilisateurs</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Nouvel utilisateur</button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Rôle</th>
              <th>Permissions</th>
              <th>Date de création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              const perms = JSON.parse(user.permissions || '{}');
              return (
                <tr key={user.id}>
                  <td style={{ fontWeight: '600' }}>{user.username}</td>
                  <td>
                    <span className={`badge ${getRoleBadge(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td>
                    {user.role === 'admin' ? (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Toutes les permissions</span>
                    ) : user.role === 'seller' ? (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Vente uniquement</span>
                    ) : (
                      <span style={{ fontSize: '13px' }}>
                        {perms.add_expenses || perms.canAddExpenses ? '✅ Dépenses' : '❌ Dépenses'}
                      </span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric', month: 'short', year: 'numeric'
                    }) : '-'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(user)}>✏️ Modifier</button>
                      {user.id !== auth.user.id && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(user)}>🗑</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editUser ? `Modifier "${editUser.username}"` : 'Nouvel utilisateur'}</h2>
            <div className="form-group">
              <label>Nom d'utilisateur</label>
              <input type="text" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="Ex: mohamed" />
            </div>
            <div className="form-group">
              <label>{editUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}</label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={editUser ? 'Laisser vide pour garder l\'ancien' : 'Mot de passe'} />
            </div>
            <div className="form-group">
              <label>Rôle</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="employee">Employé</option>
                <option value="seller">Vendeur</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>
            {form.role === 'employee' && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.permissions.add_expenses}
                    onChange={e => setForm({ ...form, permissions: { ...form.permissions, add_expenses: e.target.checked } })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  Autoriser à ajouter des dépenses
                </label>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editUser ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;
